import React, { useState, lazy } from 'react';
import { Helmet } from 'react-helmet-async';
import { Mail, Phone, MapPin, Clock, CheckCircle } from 'lucide-react';
import emailjs from '@emailjs/browser';
const ContactPage = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const {
      name,
      value
    } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      // EmailJS configuration for sending to admin
      const serviceId = 'service_93e13ll';
      const templateId = 'template_f5qtc8e'; // Contact Us template
      const publicKey = 'SgnDoSgHGRyOY5b_9'; // Your public key
      const templateParams = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message,
        time: new Date().toLocaleString() // Adding timestamp
      };
      // Send the email to admin
      await emailjs.send(serviceId, templateId, templateParams, publicKey);
      // Send confirmation email to the user
      const autoReplyTemplateId = 'template_f5qtc8e'; // You may need to create a separate auto-reply template
      const autoReplyParams = {
        to_name: formData.name,
        to_email: formData.email,
        message: "Thank you for contacting Northampton Driving School. We've received your message and will get back to you shortly."
      };
      // Send auto-reply email to the user
      await emailjs.send(serviceId, autoReplyTemplateId, autoReplyParams, publicKey);
      console.log('Emails sent successfully!');
      setIsSubmitted(true);
      setFormData({
        name: '',
        email: '',
        phone: '',
        message: ''
      });
    } catch (err) {
      console.error('Failed to send email:', err);
      setError('Failed to send your message. Please try again or contact us directly at info@northamptondrivingschool.co.uk');
    } finally {
      setIsLoading(false);
    }
  };
  return <div className="bg-white" data-id="element-1199">
      <Helmet data-id="element-1200">
        <title data-id="element-1201">
          Contact Northampton Driving School | Book Driving Lessons Today
        </title>
        <meta name="description" content="Get in touch to book automatic driving lessons in Northampton. Find answers to common questions and start your driving journey with us." data-id="element-1202" />
        <meta name="keywords" content="book driving lessons Northampton, contact driving instructor, automatic driving lessons booking, driving lesson enquiry Northampton" data-id="element-1203" />
      </Helmet>
      <div className="container mx-auto px-4 py-12" data-id="element-1204">
        <h1 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-1205">Contact Us</h1>
        <h2 className="sr-only" data-id="element-1206">Book Driving Lessons in Northampton</h2>
        <h2 className="sr-only" data-id="element-1207">Contact Northampton Driving Instructors</h2>
        <h2 className="sr-only" data-id="element-1208">Enquire About Automatic Driving Lessons</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12" data-id="element-1209">
          <div data-id="element-1210">
            <p className="text-gray-700 mb-6" data-id="element-1211">
              We're here to answer any questions you may have about our driving
              lessons. Fill out the form and we'll get back to you as soon as
              possible.
            </p>
            {isSubmitted ? <div className="bg-green-100 border-2 border-green-300 text-green-800 p-6 rounded-lg mb-6 shadow-md" data-id="element-1212">
                <div className="flex items-center mb-4" data-id="element-1213">
                  <CheckCircle size={24} className="text-green-600 mr-2" data-id="element-1214" />
                  <h3 className="text-xl font-semibold" data-id="element-1215">Thank You!</h3>
                </div>
                <p className="mb-3" data-id="element-1216">
                  Your message has been sent successfully. We'll get back to you
                  shortly.
                </p>
                <p className="text-sm" data-id="element-1217">
                  A confirmation email has been sent to your email address.
                </p>
                <button onClick={() => setIsSubmitted(false)} className="mt-4 bg-burgundy-600 hover:bg-burgundy-700 text-white px-4 py-2 rounded-md font-medium transition-colors" data-id="element-1218">
                  Send Another Message
                </button>
              </div> : <form onSubmit={handleSubmit} className="space-y-4" data-id="element-1219">
                <div data-id="element-1220">
                  <label htmlFor="name" className="block text-gray-700 font-medium mb-1" data-id="element-1221">
                    Name
                  </label>
                  <input type="text" id="name" name="name" value={formData.name} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1222" />
                </div>
                <div data-id="element-1223">
                  <label htmlFor="email" className="block text-gray-700 font-medium mb-1" data-id="element-1224">
                    Email
                  </label>
                  <input type="email" id="email" name="email" value={formData.email} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1225" />
                </div>
                <div data-id="element-1226">
                  <label htmlFor="phone" className="block text-gray-700 font-medium mb-1" data-id="element-1227">
                    Phone
                  </label>
                  <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} required className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1228" />
                </div>
                <div data-id="element-1229">
                  <label htmlFor="message" className="block text-gray-700 font-medium mb-1" data-id="element-1230">
                    Message
                  </label>
                  <textarea id="message" name="message" value={formData.message} onChange={handleChange} required rows={5} className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-burgundy-500" aria-required="true" data-id="element-1231"></textarea>
                </div>
                {error && <div className="bg-red-100 text-red-800 p-3 rounded-md" role="alert" data-id="element-1232">
                    {error}
                  </div>}
                <button type="submit" disabled={isLoading} className={`bg-burgundy-600 hover:bg-burgundy-700 text-white px-6 py-3 rounded-md font-medium transition-colors ${isLoading ? 'opacity-70 cursor-not-allowed' : ''}`} aria-label="Submit contact form" data-id="element-1233">
                  {isLoading ? 'Sending...' : 'Send Message'}
                </button>
              </form>}
          </div>
          <div data-id="element-1234">
            <div className="bg-gray-50 p-6 rounded-lg mb-6" data-id="element-1235">
              <h2 className="text-xl font-bold mb-4" data-id="element-1236">Contact Information</h2>
              <ul className="space-y-4" data-id="element-1237">
                <li className="flex items-start" data-id="element-1238">
                  <Phone size={20} className="text-burgundy-600 mr-4 mt-1" aria-hidden="true" data-id="element-1239" />
                  <div data-id="element-1240">
                    <p className="font-medium" data-id="element-1241">Phone</p>
                    <a href="tel:07481300600" className="text-burgundy-600 hover:text-burgundy-800" aria-label="Call us at 07481 300 600" data-id="element-1242">
                      07481 300 600
                    </a>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1243">
                  <Mail size={20} className="text-burgundy-600 mr-4 mt-1" aria-hidden="true" data-id="element-1244" />
                  <div data-id="element-1245">
                    <p className="font-medium" data-id="element-1246">Email</p>
                    <a href="mailto:info@northamptondrivingschool.co.uk" className="text-burgundy-600 hover:text-burgundy-800" aria-label="Email us at info@northamptondrivingschool.co.uk" data-id="element-1247">
                      info@northamptondrivingschool.co.uk
                    </a>
                  </div>
                </li>
                <li className="flex items-start" data-id="element-1248">
                  <MapPin size={20} className="text-burgundy-600 mr-4 mt-1" aria-hidden="true" data-id="element-1249" />
                  <div data-id="element-1250">
                    <p className="font-medium" data-id="element-1251">Service Area</p>
                    <p data-id="element-1252">
                      <span className="text-burgundy-600" data-id="element-1253">N</span>orthampton and
                      surrounding areas
                    </p>
                  </div>
                </li>
              </ul>
            </div>
            <div className="bg-gray-50 p-6 rounded-lg" data-id="element-1254">
              <h2 className="text-xl font-bold mb-4" data-id="element-1255">Operating Hours</h2>
              <ul className="space-y-2" data-id="element-1256">
                <li className="flex items-center" data-id="element-1257">
                  <Clock size={16} className="text-burgundy-600 mr-3" aria-hidden="true" data-id="element-1258" />
                  <span data-id="element-1259">Monday - Thursday: 9:15 AM - 7:00 PM</span>
                </li>
                <li className="flex items-center" data-id="element-1260">
                  <Clock size={16} className="text-burgundy-600 mr-3" aria-hidden="true" data-id="element-1261" />
                  <span data-id="element-1262">Friday: Closed</span>
                </li>
                <li className="flex items-center" data-id="element-1263">
                  <Clock size={16} className="text-burgundy-600 mr-3" aria-hidden="true" data-id="element-1264" />
                  <span data-id="element-1265">Saturday - Sunday: 9:15 AM - 7:00 PM</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default ContactPage;